import { setTimeout } from 'timers';

import { useState } from 'react';

import * as Sentry from '@sentry/react';
import { useHistory } from 'react-router';

import { ErrorText } from 'src/app/components/Elements/ErrorText/ErrorText';
import { useCreateLead } from 'src/app/hooks/data/lead';
import { useCheckEmailAvailability } from 'src/app/hooks/data/user';
import { useGetStartedPersistentData } from 'src/app/hooks/feature_data/useGetStartedPersistentData';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { getStartedNewContact } from 'src/app/router/paths';

import { Step } from '../../StepLayout/Step';

export const CaptchaStep = () => {
  useSegmentEvent('viewReCaptchaFallback', { shouldTrackOnMount: true });

  const trackCaptchaSuccessful = useSegmentEvent('captchaSuccessful', { shouldTrackOnMount: false });
  const trackCreateLead = useSegmentEvent('createLead', { shouldTrackOnMount: false });
  const trackCreateEligibleLead = useSegmentEvent('createEligibleLead', { shouldTrackOnMount: false });
  const trackCaptchaFailed = useSegmentEvent('captchaFailed', { shouldTrackOnMount: false });

  const title = 'Please help us protect your security';
  const grecaptcha = typeof window !== 'undefined' ? window.grecaptcha : undefined;

  const history = useHistory();

  const { mutateAsync: createLead } = useCreateLead();

  const { getStartedPersistentData } = useGetStartedPersistentData();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { mutateAsync: checkEmailAvailability } = useCheckEmailAvailability();

  const recaptchaCallback = async (captchaToken: string, email: string) => {
    try {
      await checkEmailAvailability({ email, captchaToken, captchaVersion: 'v2' });
      trackCaptchaSuccessful({ type: 'Interactive' });
      trackCreateLead();
      createLead({
        zipCode: getStartedPersistentData?.zipCode ? getStartedPersistentData.zipCode : '',
        email,
        source: 'web_onboarding_form',
      });
      trackCreateEligibleLead();
      // TODO: go to next page
    } catch (error: any) {
      if (error.response?.status === 400) {
        //email taken, kick user back to email step with error
        setTimeout(() => {
          history.replace({
            pathname: getStartedNewContact.route,
            state: { errorCode: 'existing_email' },
          });
        }, 500);
      }
      if (error.response?.status === 403) {
        //user could not pass v2 captcha, meaning we think they are a bot
        //show an error message and let them click the back button
        //but basically abandon them. death to bots.
        trackCaptchaFailed({ type: 'Interactive' });
        setErrorMessage('There was a problem validating your captcha response');
      }
    }
  };
  if (grecaptcha && getStartedPersistentData?.email) {
    grecaptcha.ready(() => {
      try {
        grecaptcha.render('recaptcha', {
          sitekey: '6LfNYVkfAAAAAN08gPmPublj7Jr-C2ZZc0YW0R67',
          callback: captchaToken => recaptchaCallback(captchaToken, getStartedPersistentData.email),
        });
      } catch (error: any) {
        /* ignore error thrown by re-rendering recaptcha */
        if (error.toString() !== 'Error: reCAPTCHA has already been rendered in this element') {
          Sentry.captureException(error);
        }
      }
    });
  }
  return (
    <Step title={title} handlePrev={() => history.push(getStartedNewContact.route)}>
      <div id="recaptcha" className="g-recaptcha" data-sitekey="6LfNYVkfAAAAAN08gPmPublj7Jr-C2ZZc0YW0R67" />
      {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
    </Step>
  );
};
